import { useMutation } from "@apollo/client";
import { NativeStackScreenProps } from "@react-navigation/native-stack";
import { CameraCapturedPicture } from "expo-camera";
import { ImagePickerAsset } from "expo-image-picker";
import { useCallback } from "react";
import { CameraProvider } from "../../../../components/CameraViewFinder/components";
import {
  DrawerRootParamList,
  PhotoImageRootParamList,
} from "../../../../types";
import { base64ToFile } from "../../../../utilities/base-64";
import { getBase64 } from "../../../../utilities/camera/utils";
import { MUTATION_UPLOAD_RETAILER_IMAGE } from "../../../../utilities/product/constants";
import { SelectUploadImageMethodScreenInner } from "../SelectUploadImageMethodScreenInner";
import { InteractionManager } from "react-native";
import { useSelectUploadImageProvider } from "../../components/SelectUploadImageProvider/hooks";
import { useGlobalSettingsProvider } from "../../../../utilities/global-settings/hooks";
import { useGracefulBackgroundRemoval } from "../../../../utilities/background-eraser";
import { useFlashMessages } from "@towersystems/roam-common-native";

export type SelectUploadImageMethodScreenProps = NativeStackScreenProps<
  PhotoImageRootParamList & DrawerRootParamList,
  "SelectUploadRetailerImageMethodScreen"
>;

export const SelectUploadRetailerImageMethodScreen = ({
  route,
  navigation,
}: SelectUploadImageMethodScreenProps) => {
  const { sku } = route.params;

  const { showMessage } = useFlashMessages();

  const { automaticBackgroundRemoval } = useGlobalSettingsProvider();

  const { gracefulRemoveBackground } = useGracefulBackgroundRemoval();

  const { handlers } = useSelectUploadImageProvider();

  const [uploadRetailerImageMutation] = useMutation(
    MUTATION_UPLOAD_RETAILER_IMAGE
  );

  const savePhoto = useCallback(
    async (
      photo: CameraCapturedPicture | ImagePickerAsset,
      removeBg?: boolean
    ): Promise<unknown> => {
      // file system URLs are not supported in the browser, falling back to uri on web provides base64 value
      const base64 = getBase64(photo);

      if (!base64 || !sku) throw Error("Missing base64 or sku");

      let file = base64ToFile(base64, `${sku}.jpg`);

      if (automaticBackgroundRemoval || removeBg) {
        const maybeFile = await gracefulRemoveBackground(base64, sku, {
          preview: false,
        });
        if (!!maybeFile) {
          file = maybeFile;
        }
      }

      return uploadRetailerImageMutation({
        variables: {
          input: {
            sku: sku,
            position: 2,
            file,
          },
        },
      })
        .then((res) => {
          handlers.onUpload(navigation);

          InteractionManager.runAfterInteractions(() => {
            navigation.navigate("ImagesSentSuccessPartialScreen", {
              sku,
            });
          });
        })
        .catch(() => {
          showMessage({
            message: "Failed to upload image",
            severity: "error",
          });
        })
        .finally(() => false);
    },
    [sku]
  );

  return (
    <CameraProvider>
      <SelectUploadImageMethodScreenInner
        {...{ route, navigation, savePhoto }}
      />
    </CameraProvider>
  );
};
